import React, { useState, FC, useEffect, useRef } from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { Card, CardContent, Typography, Button, Grid } from "@material-ui/core";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

import clsx from "clsx";
import { ILocation } from "../../models/location";

interface ILocationCardProps {
  location: ILocation;
  hideViewMoreLocations?: boolean;
  hideProviders?: boolean;
  evenCards?: boolean;
}
export const LocationCard: FC<ILocationCardProps> = ({
  location,
  evenCards,
  hideProviders,
  hideViewMoreLocations,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const classes = useStyles();
  const contentContainer = useRef<any>(null);
  const [refVisible, setRefVisible] = useState(false);

  useEffect(() => {
    if (!refVisible) {
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refVisible]);

  const handleShowMore = () => {
    setIsOpen(!isOpen);
  };

  const hasOverflow = ({ scrollHeight }: { scrollHeight: number }) => {
    return scrollHeight >= 399;
  };

  return (
    <>
      <div
        key={location.id}
        ref={(el) => {
          contentContainer.current = el;
          setRefVisible(!!el);
        }}
      >
        <Card
          className={classes.root}
          style={{ maxHeight: isOpen || !evenCards ? "100%" : 400 }}
        >
          <CardContent>
            <Typography className={classes.title} variant="h3">
              <a
                className={classes.locationLink}
                href={location.link}
                title={`Learn More About ${location.name}`}
              >
                {location.name}
              </a>
            </Typography>
            <Typography className={classes.address} variant="subtitle1">
              <a
                rel={"noreferrer"}
                target={"_blank"}
                href={`https://maps.google.com?q=${location.lat},${location.lng}`}
              >
                {location.address}
              </a>
            </Typography>
            <div className={classes.infoContainer}>
              <Typography className={classes.padding} variant="body1">
                Phone: {location.phone}
              </Typography>
              {location.fax && (
                <Typography className={classes.padding} variant="body1">
                  Fax: {location.fax}
                </Typography>
              )}
              {location.dental_fax && (
                <Typography className={classes.padding} variant="body1">
                  Dental Fax: {location.dental_fax}
                </Typography>
              )}
              {location.family_practice_fax && (
                <Typography className={classes.padding} variant="body1">
                  Family Medicine Fax: {location.family_practice_fax}
                </Typography>
              )}
              {location.pediatrics_fax && (
                <Typography className={classes.padding} variant="body1">
                  Pediatrics Fax: {location.pediatrics_fax}
                </Typography>
              )}
              {location.pharmacy_fax && (
                <Typography className={classes.padding} variant="body1">
                  Pharmacy Fax: {location.pharmacy_fax}
                </Typography>
              )}
              {location.womens_health_fax && (
                <Typography className={classes.padding} variant="body1">
                  Women's Health Fax: {location.womens_health_fax}
                </Typography>
              )}
              {location.hours && (
                <Typography className={classes.padding} variant="body1">
                  Center Hours:{" "}
                  <span
                    dangerouslySetInnerHTML={{ __html: location.hours }}
                  ></span>
                </Typography>
              )}
              {location.dental_hours && (
                <Typography className={classes.padding} variant="body1">
                  Dental Hours:{" "}
                  <span
                    dangerouslySetInnerHTML={{
                      __html: location.dental_hours,
                    }}
                  ></span>
                </Typography>
              )}
              {location.pharmacy_hours && (
                <Typography className={classes.padding} variant="body1">
                  Pharmacy Hours:{" "}
                  <span
                    dangerouslySetInnerHTML={{
                      __html: location.pharmacy_hours,
                    }}
                  ></span>
                </Typography>
              )}
            </div>
            <hr />
            <div className={classes.specialtyContainer}>
              {location.services.map((service: any) => (
                <Typography
                  key={service.ID}
                  className={clsx(classes.padding, classes.specialty)}
                  variant="body1"
                >
                  <CheckCircleIcon className={classes.icon} />
                  {service.post_title}
                </Typography>
              ))}
            </div>
            <div className={classes.providerContainer}>
              {!hideProviders &&
                location.providers?.map((provider) => {
                  return (
                    <Typography
                      key={`${location.id} ${provider.id}`}
                      className={classes.provider}
                      variant="subtitle1"
                    >
                      <a
                        rel={"noreferrer"}
                        target={"_blank"}
                        href={provider.link}
                      >
                        {provider.title.rendered}
                      </a>
                    </Typography>
                  );
                })}
            </div>
          </CardContent>
        </Card>
        {!hideViewMoreLocations && (
          <Grid className={classes.container}>
            <Button
              className={classes.viewMore}
              startIcon={<ArrowForwardIcon />}
              variant="contained"
              href="/locations"
            >
              View More Locations
            </Button>
          </Grid>
        )}
        {evenCards &&
          contentContainer &&
          contentContainer.current &&
          hasOverflow(contentContainer.current) && (
            <Grid className={classes.container}>
              <Button
                className={classes.seeMore}
                startIcon={isOpen ? <RemoveIcon /> : <AddIcon />}
                variant="contained"
                onClick={() => handleShowMore()}
              >
                {`See ${isOpen ? "Less" : "More"}`}
              </Button>
            </Grid>
          )}
      </div>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.grey[200],
    boxShadow: "none",
    position: "relative",
  },

  padding: {
    padding: ".25rem 0",
  },
  title: {
    color: theme.palette.secondary.main,
  },
  address: {
    "& a": {
      color: theme.palette.primary.light,
    },
  },
  provider: {
    "& a": {
      color: theme.palette.primary.light,
    },
    "&:last-child": {
      paddingBottom: "1rem",
    },
  },
  container: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  viewMore: {
    borderRadius: "10px 0px 10px 0px",
    boxShadow: "none",
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.grey[100],
    position: "absolute",
    marginTop: "0",
    padding: "1rem 1rem 1rem 1rem",
  },
  seeMore: {
    borderRadius: "10px 0px 10px 0px",
    boxShadow: "none",
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.grey[100],
    position: "absolute",
    marginTop: "0",
    padding: "1rem 1rem 1rem 1rem",
    zIndex: 2,
  },
  link: {
    textDecoration: "none",
    color: "#fff",
  },
  specialty: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    "&:last-child": {
      paddingBottom: "1rem",
    },
    [theme.breakpoints.down("md")]: {
      "&:last-child": {
        paddingBottom: "1.5rem",
      },
    },
  },
  infoContainer: {},
  specialtyContainer: {},
  providerContainer: {},
  icon: {
    marginRight: ".5rem",
    color: theme.palette.primary.main,
  },
  locationLink: {
    color: theme.palette.secondary.main,
    textDecoration: "none",
    "&:hover": {
      color: theme.palette.primary.light,
    },
  },
}));
